import { RefObject, useEffect, useState } from "react"
import { useWindowSize } from "./useWindowSize"

// Hook for detecting overflow of html element (e.g. checking whether div is scrollable or not).
// We use this in <Table /> component to display indicators on left and right side to indicate possible scrolling.
// From: https://github.com/amorriscode/use-overflow
export const useOverflow = (
    ref: RefObject<HTMLElement>
): {
    refXOverflowing: boolean
    refYOverflowing: boolean
    refXScrollBegin: boolean
    refXScrollEnd: boolean
    refYScrollBegin: boolean
    refYScrollEnd: boolean
} => {
    const [refXOverflowing, setRefXOverflowing] = useState(false)
    const [refYOverflowing, setRefYOverflowing] = useState(false)

    const [refXScrollBegin, setRefXScrollBegin] = useState(true)
    const [refXScrollEnd, setRefXScrollEnd] = useState(false)

    const [refYScrollBegin, setRefYScrollBegin] = useState(true)
    const [refYScrollEnd, setRefYScrollEnd] = useState(false)

    const size = useWindowSize()

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (!ref?.current) {
            return
        }
        const isXOverflowing = ref.current.scrollWidth > ref.current.clientWidth
        const isYOverflowing = ref.current.scrollHeight > ref.current.clientHeight

        if (refXOverflowing !== isXOverflowing) {
            setRefXOverflowing(isXOverflowing)
        }

        if (refYOverflowing !== isYOverflowing) {
            setRefYOverflowing(isYOverflowing)
        }

        const handleScroll = (): void => {
            // Handle X Overflow
            const offsetRight = (ref?.current?.scrollWidth ?? 0) - (ref?.current?.clientWidth ?? 0)
            if ((ref?.current?.scrollLeft ?? 0) >= offsetRight && !refXScrollEnd) {
                setRefXScrollEnd(true)
            } else {
                setRefXScrollEnd(false)
            }

            if (ref?.current?.scrollLeft === 0) {
                setRefXScrollBegin(true)
            } else {
                setRefXScrollBegin(false)
            }

            // Handle Y Overflow
            const offsetBottom = (ref?.current?.scrollHeight ?? 0) - (ref?.current?.clientHeight ?? 0)
            if ((ref?.current?.scrollTop ?? 0) >= offsetBottom && !refYScrollEnd) {
                setRefYScrollEnd(true)
            } else {
                setRefYScrollEnd(false)
            }

            if (ref?.current?.scrollTop === 0) {
                setRefYScrollBegin(true)
            } else {
                setRefYScrollBegin(false)
            }
        }

        ref.current.addEventListener("scroll", handleScroll)

        return (): void => ref.current?.removeEventListener("scroll", handleScroll)
    }, [ref, size.width]) // Empty array ensures that effect is only run on mount and unmount
    /* eslint-enable react-hooks/exhaustive-deps */

    return {
        refXOverflowing: refXOverflowing,
        refYOverflowing: refYOverflowing,
        refXScrollBegin: refXScrollBegin,
        refXScrollEnd: refXScrollEnd,
        refYScrollBegin: refYScrollBegin,
        refYScrollEnd: refYScrollEnd,
    }
}
